import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ApiService } from '@shared/services/api.service';
import { EntityType, EventType, InvoiceStatus, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventService } from '@shared/store/event/event.service';
import { INVOICE_STATUSES, InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceQuery } from '@shared/store/invoice/invoice.query';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DownloadTemplateButtonComponent,
    FileManagerComponent,
    IconComponent,
    NgSelectModule,
    AsyncPipe,
    NgClass,
    FormErrorDirective,
    ReactiveFormsModule,
    TooltipDirective,
    FormsModule,
  ],
  selector: 'aux-new-invoice-dialog',
  templateUrl: './new-invoice-dialog.component.html',
})
export class NewInvoiceDialogComponent implements AfterViewInit {
  organizationQuery = inject(OrganizationQuery);
  organizationService = inject(OrganizationService);
  purchaseOrdersQuery = inject(PurchaseOrdersQuery);
  ref = inject(
    CustomOverlayRef<
      unknown,
      { hideFirstTab?: boolean; id: string; mode: 'edit' | 'new'; vendorId?: string }
    >
  );

  private apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private eventService = inject(EventTrackerService);
  private globalEventService = inject(EventService);
  private invoiceQuery = inject(InvoiceQuery);
  private invoiceService = inject(InvoiceService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');

  activeIndex = 0;
  @ViewChild('bulkFileManager') bulkFileManager: FileManagerComponent | undefined;
  bulkFiles$: Observable<File[]> | undefined;
  @ViewChild('bulkUpdateFileManager') bulkUpdateFileManager: FileManagerComponent | undefined;
  bulkUpdateFiles$: Observable<File[]> | undefined;
  files$: Observable<File[]> | undefined;
  filteredPONumbers$ = this.purchaseOrdersQuery.selectSortedByPONumber$;
  hideFirstTab = false;
  invoice?: InvoiceModel;
  invoiceBulkUpdateTemplateType = TemplateType.BULK_INVOICE_UPDATE_TEMPLATE;
  invoiceTemplateType = TemplateType.BULK_INVOICE_TEMPLATE;
  loading$ = new BehaviorSubject(false);
  mode: 'edit' | 'new' = 'new';
  obj: { [k: string]: 'invoice' | 'supporting' } = {};
  oneInvoiceSelected = false;
  path = '';
  pdf = 'application/pdf';
  selectedPOReference = new UntypedFormControl(null);
  selectedVendor = new UntypedFormControl('', [Validators.required]);
  @ViewChild('singleFileManager') singleFileManager: FileManagerComponent | undefined;
  tabs = [{ label: 'Single Invoice' }, { label: 'Bulk Upload' }, { label: 'Bulk Update' }];
  updateValidation = new Subject();

  ngAfterViewInit() {
    if (this.singleFileManager) {
      this.files$ = this.singleFileManager.fileQuery.selectAll();
      this.updateValidation
        .pipe(
          switchMap(() => this.files$ as Observable<File[]>),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((files) => {
          files.forEach((file) => {
            if (this.obj[file.id] === undefined) {
              this.obj[file.id] = 'supporting';
            }
          });

          this.oneInvoiceSelected = files.some((file) => this.obj[file.id] === 'invoice');
        });

      this.updateValidation.next(null);
    }

    if (this.bulkFileManager) {
      this.bulkFiles$ = this.bulkFileManager.fileQuery.selectAll();
    }

    if (this.bulkUpdateFileManager) {
      this.bulkUpdateFiles$ = this.bulkUpdateFileManager.fileQuery.selectAll();
    }
  }

  constructor() {
    this.organizationService
      .get()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.ref.data?.vendorId) {
          this.selectedVendor.setValue(this.ref.data.vendorId);
          this.onChangeVendor(this.ref.data.vendorId);
        }
      });

    if (this.ref.data?.id) {
      this.mode = this.ref.data.mode;
      const invoice = this.invoiceQuery.getEntity(this.ref.data.id);

      if (invoice) {
        this.invoice = invoice;
        this.selectedVendor.setValue(invoice.organization.id);
        this.selectedPOReference.setValue(invoice.po_reference);
        this.selectedVendor.disable();
        this.selectedPOReference.disable();
      }
    }

    if (this.ref.data?.hideFirstTab) {
      this.hideFirstTab = true;
      this.activeIndex = 1;
    }
  }

  getBulkFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/bulk-invoice-templates/`;
  }

  getBulkUpdateFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/bulk-invoice-update-templates/`;
  }

  getFilePath(vendor_sub: string, invoice_sub: string, type: 'invoice' | 'supporting') {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/vendors/${vendor_sub}/invoices/${invoice_sub}/${type}/`;
  }

  onChangeVendor(selectedVendorId: string) {
    this.filteredPONumbers$ = this.purchaseOrdersQuery.selectSortedByPONumber$.pipe(
      map((value) => {
        return value.filter((po) => {
          if (selectedVendorId) {
            return po.organization_id === selectedVendorId;
          }

          return po;
        });
      })
    );
  }

  onTabChange(index: number) {
    this.activeIndex = index;
  }

  // eslint-disable-next-line max-lines-per-function
  async onUpload() {
    this.selectedVendor.markAsDirty();
    this.selectedVendor.markAsTouched();
    this.selectedVendor.updateValueAndValidity();

    if (
      this.singleFileManager &&
      !this.loading$.getValue() &&
      (this.mode === 'new' ? this.selectedVendor.valid : true)
    ) {
      this.loading$.next(true);
      let success = false;
      let errors: string[] = [];
      let data = null;
      const files = this.singleFileManager.fileQuery.getAll();
      const filesWithUpdatedBucketKey: File[] = [];

      if (this.mode === 'new') {
        const invoiceId = Utils.uuid();
        const bucket_keys = [];

        for (const file of files) {
          const bucket_key = `${this.getFilePath(
            this.selectedVendor.value,
            invoiceId as string,
            this.obj[file.id]
          )}${file.bucket_key}`;

          bucket_keys.push(bucket_key);
          filesWithUpdatedBucketKey.push({
            ...file,
            bucket_key,
          });
        }

        const resp = await this.invoiceService.add({
          bucket_keys,
          id: invoiceId,
          organization_id: this.selectedVendor.value,
          po_reference: this.selectedPOReference.value,
        });

        success = resp.success;
        errors = resp.errors;
        data = resp.data;
      } else if (this.invoice) {
        success = true;
        data = { id: this.invoice.id };
      }

      if (success && data) {
        for (const file of filesWithUpdatedBucketKey) {
          this.singleFileManager.fileStore.update(file.id, file);
        }

        const filesSuccess = await this.singleFileManager.fileService.uploadFiles({
          metadata: {
            status: INVOICE_STATUSES.STATUS_IN_QUEUE,
          },
        });

        if (filesSuccess) {
          if (this.mode === 'new' && this.oneInvoiceSelected) {
            const invoice = files.find((file) => this.obj[file.id] === 'invoice') || files[0];
            const tracking_id = Utils.uuid();

            const { errors: eventErrors, success: eventSuccess } = await firstValueFrom(
              this.globalEventService.processEvent$({
                bucket_key: `public/${this.getFilePath(
                  this.selectedVendor.value,
                  data.id as string,
                  this.obj[invoice.id]
                )}${invoice.bucket_key}`,
                entity_id: data.id,
                entity_type: EntityType.INVOICE,
                tracking_id,
                type: EventType.INVOICE_CREATED,
              })
            );

            // eslint-disable-next-line max-depth
            if (eventSuccess) {
              void this.eventService.trackEvent(tracking_id);
              this.overlayService.success(`Invoice successfully created!`);
            } else {
              // eslint-disable-next-line max-depth
              for (const file of files) {
                void this.apiService.removeFile(
                  `${this.getFilePath(
                    this.selectedVendor.value,
                    data.id as string,
                    this.obj[file.id]
                  )}${file.bucket_key}`
                );
              }

              this.overlayService.error(eventErrors);
            }
          } else if (this.mode === 'new' && !this.oneInvoiceSelected) {
            await this.invoiceService.update({
              ...data,
              audit_record_create: true,
              invoice_status: InvoiceStatus.STATUS_PENDING_REVIEW,
            } as InvoiceModel);
            await firstValueFrom(
              this.globalEventService.processEvent$({
                entity_id: data.id,
                entity_type: EntityType.INVOICE,
                type: EventType.INVOICE_PENDING_REVIEW,
              })
            );

            this.overlayService.success(`Invoice successfully created!`);
          } else {
            this.overlayService.success(`Invoice successfully updated!`);
          }

          this.ref.close(true);
        }
      } else {
        this.overlayService.error(errors);
      }
    }

    this.loading$.next(false);
  }

  async onUploadBulk(
    fileManager: FileManagerComponent | undefined,
    getFilePath: () => string,
    eventType: EventType
  ) {
    if (!fileManager) {
      console.error('No file manager set');

      return;
    }

    this.errorMessage.set('');

    if (fileManager && !this.loading$.getValue()) {
      const files = fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      this.loading$.next(true);

      const file = files[0];
      const key = `${getFilePath.call(this)}${file.bucket_key}`;

      fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await fileManager.fileService.uploadFiles({});

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.globalEventService.processEvent$({
            bucket_key: `public/${key}`,
            entity_id: this.mainQuery.getValue().trialKey,
            entity_type: EntityType.INVOICE,
            type: eventType,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          void this.apiService.removeFile(key);
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  removeBulkFile(file: File) {
    if (this.bulkFileManager) {
      void this.bulkFileManager.removeFile(file);
    }
  }

  removeBulkUpdateFile(file: File) {
    if (this.bulkUpdateFileManager) {
      void this.bulkUpdateFileManager.removeFile(file);
    }
  }

  removeFile(file: File) {
    if (this.singleFileManager) {
      void this.singleFileManager.removeFile(file);
    }
  }

  async uploadInvoice() {
    if (this.activeIndex === 0) {
      await this.onUpload();
    } else if (this.activeIndex === 1) {
      await this.onUploadBulk(
        this.bulkFileManager,
        this.getBulkFilePath,
        EventType.BULK_INVOICE_TEMPLATE_UPLOADED
      );
    } else if (this.activeIndex === 2) {
      await this.onUploadBulk(
        this.bulkUpdateFileManager,
        this.getBulkUpdateFilePath,
        EventType.BULK_INVOICE_UPDATE_TEMPLATE_UPLOADED
      );
    }
  }
}
